/**
 * Booking API Service
 * Centralized API calls for booking operations
 * Replaces Redux epics with simple fetch functions
 */

import config from '../config';
import authService from './auth';
import type { Booking, ApiError } from '../types';

const BASE_URL = config.apiBaseUrl;

/**
 * Helper to get auth token
 */
const getAuth = (): string | null => authService.getToken();

/**
 * Fetch options type
 */
interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Helper for fetch with error handling
 */
async function fetchApi<T>(url: string, options: FetchOptions = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = new Error(`API Error: ${response.status} ${response.statusText}`) as ApiError;
    error.status = response.status;
    throw error;
  }

  // Handle empty responses
  const text = await response.text();
  return text ? JSON.parse(text) as T : (null as unknown as T);
}

/**
 * Load booking by UUID
 * GET /load/uuid/{uuid}/{auth}
 */
export async function fetchBooking(uuid: string): Promise<Booking> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('No authentication token available');
  }
  return fetchApi<Booking>(`${BASE_URL}load/uuid/${uuid}/${auth}`);
}

/**
 * Check booking session status
 * GET /{uuid}/session/check/{auth}
 */
export async function checkBookingSession(uuid: string): Promise<Booking> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('No authentication token available');
  }
  return fetchApi<Booking>(`${BASE_URL}${uuid}/session/check/${auth}`);
}

/**
 * Accept booking (on-account customers)
 * POST /{id}/onAccountTrigger/{auth}
 */
export async function acceptBooking(bookingId: number): Promise<void> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('No authentication token available');
  }
  return fetchApi<void>(`${BASE_URL}${bookingId}/onAccountTrigger/${auth}`, {
    method: 'POST',
  });
}

/**
 * Decline booking
 * POST /{id}/declined/{auth}
 */
export async function declineBooking(bookingId: number): Promise<void> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('No authentication token available');
  }
  return fetchApi<void>(`${BASE_URL}${bookingId}/declined/${auth}`, {
    method: 'POST',
  });
}

/**
 * Accept booking with payment verification
 * POST /{id}/accept/verify/{auth}
 */
export async function acceptBookingWithVerification(bookingId: number): Promise<void> {
  const auth = getAuth();
  if (!auth) {
    throw new Error('No authentication token available');
  }
  return fetchApi<void>(`${BASE_URL}${bookingId}/accept/verify/${auth}`, {
    method: 'POST',
  });
}

// Export all API functions
export const bookingApi = {
  fetchBooking,
  checkBookingSession,
  acceptBooking,
  declineBooking,
  acceptBookingWithVerification,
};

export default bookingApi;
